import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from "@mui/material";
import API from "../../services/API";
import { AppButton, AppTextField, Panel } from "../ui";

const CollectionSystemsSection = ({ systems }) => {
  const [systemsState, setSystemsState] = useState(systems || []);
  const [editingId, setEditingId] = useState(null);
  const [editedSystems, setEditedSystems] = useState({});

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [systemToDelete, setSystemToDelete] = useState(null);

  useEffect(() => {
    setSystemsState(systems || []);
  }, [systems]);

  const handleEdit = async (systemId) => {
    try {
      const response = await API.get(`systems/${systemId}`, {
        requiresAuth: true,
      });
      const fullSystem = response.data;
      setEditingId(systemId);
      setEditedSystems((prev) => ({
        ...prev,
        [systemId]: {
          name: fullSystem.name,
          description: fullSystem.description,
          fullSystem: fullSystem,
        },
      }));
    } catch (error) {
      console.error("Error fetching full system data:", error);
    }
  };

  const handleChange = (systemId, field, value) => {
    setEditedSystems((prev) => ({
      ...prev,
      [systemId]: {
        ...prev[systemId],
        [field]: value,
      },
    }));
  };

  const handleCancel = () => {
    setEditingId(null);
  };

  const handleSave = async (systemId) => {
    const currentData = editedSystems[systemId];
    if (!currentData || !currentData.fullSystem) return;
    const updatedSystem = {
      ...currentData.fullSystem,
      name: currentData.name,
      description: currentData.description,
    };

    try {
      await API.post("systems/", updatedSystem, { requiresAuth: true });
      setSystemsState((prev) =>
        prev.map((sys) =>
          sys.id === systemId
            ? {
                ...sys,
                name: updatedSystem.name,
                description: updatedSystem.description,
              }
            : sys
        )
      );
      setEditingId(null);
    } catch (error) {
      console.error("Error saving system:", error);
    }
  };

  const handleDeleteClick = (system) => {
    setSystemToDelete(system);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!systemToDelete) return;
    try {
      await API.get(`systems/${systemToDelete.id}/delete`, {
        requiresAuth: true,
      });
      setSystemsState((prev) => prev.filter((sys) => sys.id !== systemToDelete.id));
    } catch (error) {
      console.error("Error deleting system:", error);
    } finally {
      setDeleteConfirmOpen(false);
      setSystemToDelete(null);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmOpen(false);
    setSystemToDelete(null);
  };

  return (
    <section className="collectionDetailSection">
      <Typography className="collectionDetailSectionTitle" variant="h5">
        Systems ({systemsState.length})
      </Typography>

      <div className="systemsList app-row-list">
        {systemsState.map((system) => (
          <Panel key={system.id} className="systemCard" hoverable>
            {editingId === system.id ? (
              <div className="systemEditForm">
                <div className="systemEditFields">
                  <AppTextField
                    label="Display Name"
                    value={editedSystems[system.id]?.name || ""}
                    onChange={(e) => handleChange(system.id, "name", e.target.value)}
                    fullWidth
                  />
                  <AppTextField
                    label="Description"
                    value={editedSystems[system.id]?.description || ""}
                    onChange={(e) => handleChange(system.id, "description", e.target.value)}
                    fullWidth
                  />
                </div>

                <div className="systemEditActions">
                  <AppButton onClick={() => handleSave(system.id)}>
                    Save
                  </AppButton>
                  <AppButton kind="ghost" onClick={handleCancel}>
                    Cancel
                  </AppButton>
                </div>
              </div>
            ) : (
              <div className="systemCardContent">
                <div className="systemCardInfo">
                  <div className="systemCardName">{system.name || "No Name"}</div>
                  {system.status?.type && (
                    <div className="systemCardStatus">{system.status?.type}</div>
                  )}
                </div>

                <div className="systemCardDesc">
                  {system.description || "No Description"}
                </div>

                <div className="systemCardActions">
                  <AppButton
                    className="systemCardBtn"
                    kind="secondary"
                    onClick={() => handleEdit(system.id)}
                  >
                    Edit
                  </AppButton>
                  <AppButton
                    className="systemCardBtn"
                    kind="danger"
                    onClick={() => handleDeleteClick(system)}
                  >
                    Delete
                  </AppButton>
                </div>
              </div>
            )}
          </Panel>
        ))}
      </div>

      <Dialog open={deleteConfirmOpen} onClose={cancelDelete}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{" "}
            {systemToDelete ? systemToDelete.name || "this system" : "this system"}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <AppButton kind="ghost" onClick={cancelDelete}>
            Cancel
          </AppButton>
          <AppButton kind="danger" onClick={confirmDelete}>
            Delete
          </AppButton>
        </DialogActions>
      </Dialog>
    </section>
  );
};

export default CollectionSystemsSection;
